import { useEffect, useState } from "react";
import clsx from "clsx";
import { getConsumableIconPath, isUsableStashItem, CONSUMABLE_MAX_USE_AT_ONCE } from "../utils/assets";
import type { ConsumableItem } from "../types";
import { AdminAssetPath } from "./AdminAssetPath";
import { ModalBackdrop } from "./ModalBackdrop";

interface ConsumableDetailModalProps {
  item: ConsumableItem;
  onClose: () => void;
  onUse?: (quantity: number) => void;
  onOpenHero?: () => void;
  actionLoading?: boolean;
}

const QUICK_AMOUNTS = [1, 5, 10, 25] as const;

export function ConsumableDetailModal({
  item,
  onClose,
  onUse,
  onOpenHero,
  actionLoading,
}: ConsumableDetailModalProps) {
  const isRename = item.item_id === "rename_scroll";
  const canUse = isUsableStashItem(item.item_id);
  const maxQty = Math.max(1, Math.min(item.quantity, CONSUMABLE_MAX_USE_AT_ONCE));
  const [useQty, setUseQty] = useState(1);

  useEffect(() => {
    setUseQty((prev) => Math.min(Math.max(1, prev), maxQty));
  }, [item.item_id, maxQty]);

  const clampQty = (n: number) => Math.min(maxQty, Math.max(1, n));

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="w-full max-w-sm bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="shrink-0 space-y-1">
            <img
              src={getConsumableIconPath(item.item_id)}
              alt={item.name}
              className="w-16 h-16 rounded-lg border border-kingdom-mid object-contain bg-white"
            />
            <AdminAssetPath path={getConsumableIconPath(item.item_id)} className="max-w-[4.5rem]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-white truncate">{item.name}</div>
            <div className="text-xs text-kingdom-glow font-medium">
              Consumable{item.quantity > 1 ? ` ×${item.quantity}` : ""}
            </div>
            {item.description && <p className="text-xs text-gray-400 mt-1">{item.description}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 text-xl">
            ✕
          </button>
        </div>

        {!isRename && canUse && maxQty > 1 && (
          <div className="space-y-2 pt-1 border-t border-kingdom-mid/60">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-400">
                Amount to use
                {item.quantity > CONSUMABLE_MAX_USE_AT_ONCE && (
                  <span className="text-gray-500"> (max {CONSUMABLE_MAX_USE_AT_ONCE} per use)</span>
                )}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={actionLoading || useQty <= 1}
                  onClick={() => setUseQty((q) => clampQty(q - 1))}
                  className="w-8 h-8 rounded-lg bg-kingdom-dark border border-kingdom-mid text-white disabled:opacity-40"
                >
                  −
                </button>
                <input
                  type="number"
                  min={1}
                  max={maxQty}
                  value={useQty}
                  disabled={actionLoading}
                  onChange={(e) => setUseQty(clampQty(parseInt(e.target.value, 10) || 1))}
                  className="w-14 text-center py-1 rounded-lg bg-kingdom-dark border border-kingdom-mid text-sm text-white tabular-nums"
                />
                <button
                  type="button"
                  disabled={actionLoading || useQty >= maxQty}
                  onClick={() => setUseQty((q) => clampQty(q + 1))}
                  className="w-8 h-8 rounded-lg bg-kingdom-dark border border-kingdom-mid text-white disabled:opacity-40"
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_AMOUNTS.filter((n) => n <= maxQty).map((n) => (
                <button
                  key={n}
                  type="button"
                  disabled={actionLoading}
                  onClick={() => setUseQty(n)}
                  className={clsx(
                    "px-2.5 py-1 rounded-lg text-xs font-semibold border",
                    useQty === n
                      ? "border-kingdom-glow text-kingdom-glow bg-kingdom-glow/10"
                      : "border-kingdom-mid text-gray-400",
                  )}
                >
                  {n}
                </button>
              ))}
              {maxQty > 1 && (
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => setUseQty(maxQty)}
                  className={clsx(
                    "px-2.5 py-1 rounded-lg text-xs font-semibold border",
                    useQty === maxQty
                      ? "border-kingdom-glow text-kingdom-glow bg-kingdom-glow/10"
                      : "border-kingdom-mid text-gray-400",
                  )}
                >
                  Max ({maxQty})
                </button>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {isRename && onOpenHero && (
            <button
              onClick={onOpenHero}
              disabled={actionLoading}
              className="flex-1 py-2.5 rounded-xl bg-kingdom-glow text-black font-bold text-sm disabled:opacity-50"
            >
              Open Hero screen
            </button>
          )}
          {!isRename && canUse && onUse && (
            <button
              onClick={() => onUse(useQty)}
              disabled={actionLoading}
              className={clsx(
                "flex-1 py-2.5 rounded-xl bg-kingdom-glow text-black font-bold text-sm disabled:opacity-50",
              )}
            >
              {useQty > 1 ? `Use ×${useQty}` : "Use"}
            </button>
          )}
          {!isRename && !canUse && (
            <p className="w-full text-center text-xs text-gray-500 py-2">Cannot be used yet.</p>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}
